import type { MenuItem, SalonRequest, SubmitRequestInput } from '../types'

export const NOTE_MAX_LENGTH = 240
export const RATE_LIMIT_COUNT = 5
export const RATE_LIMIT_WINDOW_MS = 10 * 60 * 1000

export class RequestPolicyError extends Error {
  constructor(
    message: string,
    public readonly code: 'invalid' | 'unavailable' | 'rate_limited',
  ) {
    super(message)
  }
}

export function validateSubmission(
  input: SubmitRequestInput,
  menu: MenuItem[],
  requests: SalonRequest[],
  pauseNonessential: boolean,
  now = Date.now(),
) {
  const item = menu.find((candidate) => candidate.id === input.itemId)
  if (!item || !item.enabled || item.archived) {
    throw new RequestPolicyError('That request is not available right now.', 'unavailable')
  }
  if (pauseNonessential && item.category !== 'comfort') {
    throw new RequestPolicyError('Nonessential requests are paused for the moment.', 'unavailable')
  }
  const notes = input.notes.trim()
  if (notes.length > NOTE_MAX_LENGTH) {
    throw new RequestPolicyError(`Notes must be ${NOTE_MAX_LENGTH} characters or fewer.`, 'invalid')
  }
  if (!input.location.trim() || input.location.length > 80) {
    throw new RequestPolicyError('Choose a valid station.', 'invalid')
  }
  if (!input.sessionId || !input.idempotencyKey) {
    throw new RequestPolicyError('Your session could not be verified. Refresh and try again.', 'invalid')
  }
  if (item.priceCents > 0 && !input.paidAcknowledged) {
    throw new RequestPolicyError('Please acknowledge the item price before sending.', 'invalid')
  }
  const recentCount = requests.filter(
    (request) => request.sessionId === input.sessionId && now - new Date(request.createdAt).getTime() < RATE_LIMIT_WINDOW_MS,
  ).length
  if (recentCount >= RATE_LIMIT_COUNT) {
    throw new RequestPolicyError('You have sent several requests. Please wait a few minutes or speak with your stylist.', 'rate_limited')
  }
  return { item, notes }
}

export const visibleRequestsForSession = (requests: SalonRequest[], sessionId: string) =>
  requests.filter((request) => request.sessionId === sessionId)

export const findDuplicateRequest = (requests: SalonRequest[], sessionId: string, idempotencyKey: string) =>
  requests.find((request) => request.sessionId === sessionId && request.idempotencyKey === idempotencyKey)
